/// <reference types="vite/client" />

import { minigameRegistry } from '@shared/minigames/registry';
import type { MinigameClientHandle, MinigameStartArgs } from '@shared/minigames/registry';
import type { ServerMessage } from '@shared/protocol';

function renderForceMinigameError(message: string): void {
  const container = document.getElementById('app') ?? document.body;
  const panel = document.createElement('section');
  panel.style.cssText =
    'position:fixed;inset:24px;z-index:1000;display:grid;place-content:center;padding:24px;background:rgba(30,35,50,0.82);border:2px solid rgba(255,255,255,0.10);border-radius:12px;color:#FFFFFF;font:700 18px system-ui,sans-serif;text-align:center;text-shadow:2px 2px 0 #000;';
  panel.textContent = message;
  container.replaceChildren(panel);
}

async function bootForcedDevMinigame(): Promise<void> {
  if (!import.meta.env.DEV) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get('god') === '1' || readGodModeTokenFromHash(window.location.hash) != null) return;

  const id = params.get('force-minigame');
  if (id === null || id === '') return;

  await import('@minigames/index');
  if (minigameRegistry.get(id) === undefined) {
    renderForceMinigameError(`Minigame '${id}' not registered or not available`);
    return;
  }

  const { bootDevMinigameHarness } = await import('./dev-minigame-harness');
  bootDevMinigameHarness({
    id,
    container: document.getElementById('app') ?? document.body,
  });
}

void bootForcedDevMinigame();

interface ClientState {
  ws: WebSocket | null;
  playerId: string | null;
  playerName: string;
  roomCode: string;
  activeMinigame: MinigameClientHandle | null;
  messages: string[];
}

interface MinigameResultsReceiver extends MinigameClientHandle {
  applyRoundResults?(
    roundIndex: number,
    placements: ReadonlyArray<{
      readonly playerId: string;
      readonly rank: number;
      readonly count: number;
      readonly score: number;
    }>,
    phaseId?: string,
  ): void;
  applyServerResults?(
    placements: ReadonlyArray<{
      readonly playerId: string;
      readonly rank: number;
      readonly score: number;
    }>,
  ): void;
}

const CLIENT_MINIGAME_INPUT_DEBUG_LOGS = false;

interface ClientMinigameStartArgs extends MinigameStartArgs {
  readonly localPlayerId?: string;
  readonly onSubmit: (input: unknown, roundIndex: number) => void;
}

const ACTIVE_MINIGAME_PHASES = new Set([
  'minigame_announce',
  'minigame_countdown',
  'minigame_play',
  'minigame_results',
]);

function wsUrl(): string {
  const explicit = new URLSearchParams(window.location.search).get('ws');
  if (explicit !== null && explicit !== '') return explicit;
  return deriveWsUrl();
}

function appendLog(state: ClientState, log: HTMLPreElement, text: string): void {
  state.messages.push(text);
  if (state.messages.length > 80) state.messages.shift();
  log.textContent = state.messages.join('\n');
}

function disposeActiveMinigame(state: ClientState): void {
  state.activeMinigame?.dispose();
  state.activeMinigame = null;
}

async function mountMinigame(
  state: ClientState,
  message: Extract<ServerMessage, { type: 'minigame_start' }>,
): Promise<void> {
  await import('@minigames/index');
  disposeActiveMinigame(state);
  const minigame = minigameRegistry.get(message.minigameId);
  if (minigame === undefined) return;
  state.activeMinigame = minigame.startClient({
    players: message.players,
    seed: message.seed,
    localPlayerId: state.playerId ?? undefined,
    onReady: () => {
      state.ws?.send(
        JSON.stringify({
          type: 'minigame_ready',
          minigameId: message.minigameId,
          instanceId: message.instanceId,
        }),
      );
    },
    onSubmit: (input: unknown, roundIndex: number) => {
      if (CLIENT_MINIGAME_INPUT_DEBUG_LOGS) {
        console.warn('[client] outgoing minigame_input', {
          playerId: state.playerId,
          roundIndex,
          input,
        });
      }
      const payload =
        typeof input === 'object' && input !== null
          ? { ...input, roundIndex }
          : { type: 'count', count: input, roundIndex };
      state.ws?.send(
        JSON.stringify({
          type: 'minigame_input',
          minigameId: message.minigameId,
          instanceId: message.instanceId,
          clientCommandMs: performance.now(),
          payload,
        }),
      );
    },
  } as ClientMinigameStartArgs);
}

function renderMatchClient(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get('force-minigame')) return;

  const container = document.getElementById('app') ?? document.body;
  const panel = document.createElement('section');
  panel.style.cssText =
    'position:fixed;inset:16px;z-index:10;display:grid;grid-template-rows:auto auto 1fr;gap:12px;padding:16px;background:#1e2332;color:#fff;font:700 14px system-ui,sans-serif;';

  const controls = document.createElement('div');
  controls.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;align-items:center;';
  const nameInput = document.createElement('input');
  nameInput.value = params.get('name') ?? `Player${Math.floor(Math.random() * 1000)}`;
  nameInput.placeholder = 'Name';
  const codeInput = document.createElement('input');
  codeInput.placeholder = 'Code';
  codeInput.value = params.get('code') ?? '';
  const createButton = document.createElement('button');
  createButton.textContent = 'Create Minigame Room';
  const joinButton = document.createElement('button');
  joinButton.textContent = 'Join';
  const startButton = document.createElement('button');
  startButton.textContent = 'Start';
  const forceButton = document.createElement('button');
  forceButton.textContent = 'Force Animal Count';
  controls.append(nameInput, codeInput, createButton, joinButton, startButton, forceButton);

  const status = document.createElement('div');
  status.dataset.testid = 'match-status';
  status.textContent = 'Disconnected';
  const log = document.createElement('pre');
  log.dataset.testid = 'match-log';
  log.style.cssText =
    'min-height:0;overflow:auto;margin:0;padding:12px;background:#111826;color:#dbeafe;';
  panel.append(controls, status, log);
  container.replaceChildren(panel);

  const state: ClientState = {
    ws: null,
    playerId: null,
    playerName: nameInput.value,
    roomCode: codeInput.value,
    activeMinigame: null,
    messages: [],
  };

  const connect = (): WebSocket => {
    if (state.ws?.readyState === WebSocket.OPEN) return state.ws;
    const ws = new WebSocket(wsUrl());
    state.ws = ws;
    status.textContent = 'Connecting';
    ws.addEventListener('open', () => {
      status.textContent = 'Connected';
      appendLog(state, log, 'ws open');
    });
    ws.addEventListener('message', (event) => {
      const message = JSON.parse(String(event.data)) as ServerMessage;
      appendLog(state, log, JSON.stringify(message));
      if (message.type === 'room_created') {
        state.playerId = message.hostId;
        state.roomCode = message.code;
        codeInput.value = message.code;
        status.textContent = `Room ${message.code}`;
      } else if (message.type === 'room_joined') {
        state.playerId = message.playerId;
        state.roomCode = message.roomCode;
        codeInput.value = message.roomCode;
        status.textContent = `Room ${message.roomCode}`;
      } else if (message.type === 'phase_change') {
        status.textContent = `Phase ${message.phase}`;
        if (!ACTIVE_MINIGAME_PHASES.has(message.phase)) {
          disposeActiveMinigame(state);
        }
      } else if (message.type === 'minigame_start') {
        status.textContent = `Minigame ${message.displayName}`;
        void mountMinigame(state, message);
      } else if (message.type === 'minigame_selection') {
        state.ws?.send(
          JSON.stringify({
            type: 'minigame_selection_complete',
            minigameId: message.minigameId,
            instanceId: message.instanceId,
          }),
        );
      } else if (message.type === 'minigame_round_results') {
        console.warn('[client] incoming minigame_round_results', {
          roundIndex: message.roundIndex,
          placements: message.placements,
        });
        (state.activeMinigame as MinigameResultsReceiver | null)?.applyRoundResults?.(
          message.roundIndex,
          message.placements,
          message.phaseId,
        );
      } else if (message.type === 'minigame_results') {
        console.warn('[client] incoming minigame_results', { placements: message.placements });
        (state.activeMinigame as MinigameResultsReceiver | null)?.applyServerResults?.(
          message.placements,
        );
        status.textContent = `Results ${message.placements.map((p) => `${p.playerId}:${p.score}`).join(',')}`;
      } else if (message.type === 'minigame_ready_state') {
        state.activeMinigame?.applyReadyState?.(message);
      } else if (message.type === 'minigame_event') {
        state.activeMinigame?.applyLiveEvent?.(message);
      }
    });
    ws.addEventListener('close', () => {
      status.textContent = 'Disconnected';
      disposeActiveMinigame(state);
    });
    return ws;
  };

  createButton.addEventListener('click', () => {
    state.playerName = nameInput.value;
    connect().addEventListener(
      'open',
      () => {
        state.ws?.send(
          JSON.stringify({
            type: 'create_room',
            playerName: state.playerName,
            mode: 'minigames_only',
            roomType: 'create_game',
          }),
        );
      },
      { once: true },
    );
    if (state.ws?.readyState === WebSocket.OPEN) {
      state.ws.send(
        JSON.stringify({
          type: 'create_room',
          playerName: state.playerName,
          mode: 'minigames_only',
          roomType: 'create_game',
        }),
      );
    }
  });

  joinButton.addEventListener('click', () => {
    state.playerName = nameInput.value;
    state.roomCode = codeInput.value;
    const ws = connect();
    const sendJoin = (): void => {
      ws.send(
        JSON.stringify({ type: 'join_room', code: state.roomCode, playerName: state.playerName }),
      );
    };
    if (ws.readyState === WebSocket.OPEN) sendJoin();
    else ws.addEventListener('open', sendJoin, { once: true });
  });

  startButton.addEventListener('click', () => {
    state.ws?.send(JSON.stringify({ type: 'start_game' }));
  });

  forceButton.addEventListener('click', () => {
    state.ws?.send(
      JSON.stringify({ type: 'admin_force_minigame_now', minigameId: 'animal-count' }),
    );
  });
}

// Gate the minigame match dev harness behind ?match=1 so the default URL
// boots the regular game via bootMain() below.
if (new URLSearchParams(window.location.search).get('match') === '1') {
  renderMatchClient();
}

export * from './assets/index';
export * from './renderer/index';

import { bootMain, deriveWsUrl } from './main';
import type { MainBootHandle } from './main';
import { readStoredSession } from './session-storage';
import { applyNativeWarmupEntry, applyPortalEntry, detectPortalEntry } from './portal-entry';
import type { PortalEntry } from './portal-entry';
import { mountVibejamPortalButton } from './ui/components/vibejam-portal-button';
import {
  readGodModeTokenFromHash,
  resolveGodModeBootRequest,
  scrubGodModeHash,
} from './god-mode-token';
import { queueGodModeForcedMinigame } from './god-mode-force-minigame';

type AtmosphereTunerModule = {
  mountAtmosphereTuner(handle: MainBootHandle, options?: { mapName?: string }): void;
};

function isAtmosphereTunerRequested(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).get('fx') === 'tune';
}

function appendGodModeQuery(wsUrl: string): string {
  const url = new URL(wsUrl, window.location.href);
  url.searchParams.set('god', '1');
  return url.toString();
}

function appendPortalQuery(wsUrl: string, entry: PortalEntry): string {
  const url = new URL(wsUrl, window.location.href);
  url.searchParams.set('portal', 'true');
  if (entry.colorHex != null) url.searchParams.set('color', entry.colorHex);
  if (entry.ref != null) url.searchParams.set('ref', entry.ref);
  if (entry.username != null) url.searchParams.set('username', entry.username);
  return url.toString();
}

if (typeof window !== 'undefined' && !import.meta.env?.SSR) {
  const appEl = document.querySelector('#app') as HTMLElement | null;
  const uiEl = document.querySelector('#ui-container') as HTMLElement | null;
  const canvas = document.querySelector('#game-canvas') as HTMLCanvasElement | null;
  if (!appEl || !canvas || !uiEl) {
    console.error(
      '[smashparty] Boot aborted: #app, #game-canvas, or #ui-container not found in DOM',
    );
  } else {
    const godModeRequest = resolveGodModeBootRequest({
      search: window.location.search,
      hash: window.location.hash,
      dev: import.meta.env.DEV,
    });
    if (godModeRequest.token != null) {
      scrubGodModeHash(window.location, window.history);
    }
    const atmosphereTunerRequested = isAtmosphereTunerRequested();
    const portalEntry = detectPortalEntry();
    const baseWsUrl =
      portalEntry == null ? deriveWsUrl() : appendPortalQuery(deriveWsUrl(), portalEntry);
    void bootMain({
      canvas,
      host: uiEl,
      wsUrl:
        godModeRequest.enabled && godModeRequest.token == null
          ? appendGodModeQuery(baseWsUrl)
          : baseWsUrl,
    }).then(async (handle) => {
      (globalThis as { __smashparty?: MainBootHandle }).__smashparty = handle;
      mountVibejamPortalButton(handle);
      if (readStoredSession() == null) {
        if (portalEntry != null) {
          applyPortalEntry(handle, portalEntry);
        } else {
          applyNativeWarmupEntry(handle);
        }
      }
      if (godModeRequest.enabled) {
        if (godModeRequest.forceMinigameId != null) {
          queueGodModeForcedMinigame({
            wsClient: handle.wsClient,
            minigameId: godModeRequest.forceMinigameId,
            waitForGodModeEnabled: godModeRequest.token != null,
          });
        }
        if (godModeRequest.token != null) {
          handle.wsClient.send({ type: 'admin_god_auth', token: godModeRequest.token });
        }
        const { mountGodModePanel } = await import('./dev/god-mode-panel');
        mountGodModePanel({ handle });
      }
      if (atmosphereTunerRequested) {
        const tunerPath = './dev/atmosphere-tuner-panel';
        const { mountAtmosphereTuner } = (await import(
          /* @vite-ignore */ tunerPath
        )) as AtmosphereTunerModule;
        mountAtmosphereTuner(handle, { mapName: 'skyforge' });
      }
    });
  }
}
